import type {
  ActionType,
  Address,
  Applicant,
  ApplicantEntry,
  ApplicantExpense,
  ApplicantFinancials,
  ApplicantIncome,
  ApplicantTypeEnum,
  AppUser,
  AssetAccount,
  CompanyIncome,
  EntityIncome,
  Financials,
  IncomingProperty,
  LoanApplication,
  OutgoingProperty,
  PeriodicValue,
  PropertyAsset,
  SupportingProperty,
  RetirementDetails,
} from '@/types';
import { mapValues, omit } from 'lodash-es';
import { BorrowerRole } from '@/types/BorrowerGroup';
import type { BorrowerApplicant, BorrowerGroup, BorrowerRoleEnum } from '@/types/BorrowerGroup';
import {
  ApplicantType,
  AssetType,
  BorrowerType,
  Gender,
  NET_PROFIT_DEFAULT_FREQUENCY,
  RelationshipStatus,
  TrusteeType,
} from './const';
import type { BorrowerTypeEnum } from './const';
import type { UtmMetricDto } from '@/api/backend/users/applications';
import type { AdditionalFundsRequiredDto } from '@/api/backend/public';
import type { UnaccreditedBrokerProfile } from '@/types/UnaccreditedBrokerProfile';

// Generate a unique ID with a prefix using crypto.randomUUID()
// This guarantees uniqueness without needing to track existing items
export const generateUniqueId = (prefix: string): string => {
  const uuid = crypto.randomUUID().slice(0, 8);
  return `${prefix}-${uuid}`;
};

export const createApplicant = (type: ApplicantTypeEnum = ApplicantType.SECONDARY): ApplicantEntry => {
  if (type === ApplicantType.PRIMARY) {
    return { id: 'applicant-0', type };
  }
  return { id: generateUniqueId('applicant'), type };
};

/**
 * Checks `allowNoContractOfSale` from the URL
 * @returns {boolean}
 */
export const allowNoContractOfSale = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('allowNoContractOfSale') === 'true';
};

// Helper to map applicants to BorrowerApplicant format
export const mapToApplicants = (individuals: ApplicantEntry[], role: BorrowerRoleEnum): BorrowerApplicant[] => {
  return individuals
    .filter((d) => d.firstName && d.lastName)
    .map((d) => ({
      id: `${d.id}-${role}`,
      applicantId: d.id,
      role,
    }));
};

/**
 * Property type labels for the ownership step header
 */
export const PropertyOwnershipTypeLabels: Record<import('@/types/PropertyOwnership').PropertyOwnershipType, string> = {
  incoming: 'Purchase Property',
  outgoing: 'Sale Property',
  supporting: 'Supporting Property',
  asset: 'Asset Property',
} as const;

/**
 * Gets the formatted property header for the borrower ownership UI.
 * Format: "Purchase Property - QLD 4558" (type + state, postcode)
 */
export const getPropertyOwnershipHeader = (
  type: import('@/types/PropertyOwnership').PropertyOwnershipType,
  address?: Address,
): string => {
  const label = PropertyOwnershipTypeLabels[type] ?? 'Property';
  if (!address?.state && !address?.postcode) {
    return label;
  }
  const location = [address?.state, address?.postcode].filter(Boolean).join(' ');
  return location ? `${label} - ${location}` : label;
};

/**
 * Gets the display name for a borrower group based on its type.
 * - Individual: "firstName lastName"
 * - Company: "companyName"
 * - Trust (Individual Trustee): "trustee1 & trustee2 ATF trustName"
 * - Trust (Company Trustee): "companyName ATF trustName"
 */
export const getBorrowerDisplayName = (group: BorrowerGroup, applicants: ApplicantEntry[]): string => {
  if (group.type === BorrowerType.INDIVIDUAL) {
    const applicantId = group.applicants?.[0]?.applicantId;
    const applicant = applicants.find((a) => a.id === applicantId);
    return applicant ? `${applicant.firstName ?? ''} ${applicant.lastName ?? ''}`.trim() : '';
  }

  if (group.type === BorrowerType.COMPANY) {
    return group.companyDetails?.companyName ?? '';
  }

  if (group.type === BorrowerType.TRUST) {
    const trustName = group.trustDetails?.trustName ?? '';

    if (group.trustDetails?.trusteeType === TrusteeType.COMPANY) {
      const companyName = group.trustDetails?.companyDetails?.companyName ?? '';
      return companyName && trustName ? `${companyName} ATF ${trustName}` : companyName || trustName;
    } else {
      // Individual trustees
      const trusteeNames =
        group.applicants
          ?.map((ba) => {
            const applicant = applicants.find((a) => a.id === ba.applicantId);
            return applicant ? `${applicant.firstName ?? ''} ${applicant.lastName ?? ''}`.trim() : '';
          })
          .filter(Boolean)
          .join(' & ') ?? '';
      return trusteeNames && trustName ? `${trusteeNames} ATF ${trustName}` : trusteeNames || trustName;
    }
  }

  return '';
};

const BORROWER_ROLE_LABELS: Record<BorrowerRoleEnum, string> = {
  individual: 'Individual Borrower',
  director: 'Director',
  trustee: 'Trustee',
  partner: 'Partner',
};

export interface ApplicantEntityMembership {
  name: string;
  role: BorrowerRoleEnum;
  roleLabel: string;
}

/**
 * Returns the company/trust entities (and the applicant's role within each) that a
 * given applicant belongs to. Individual borrower groups are excluded — only
 * company/trust memberships add context worth surfacing next to the applicant.
 */
export const getApplicantEntityMemberships = (
  applicantId: string,
  borrowerGroups: BorrowerGroup[] | undefined,
  applicants: ApplicantEntry[],
): ApplicantEntityMembership[] => {
  return (borrowerGroups ?? [])
    .filter((group) => group.type !== BorrowerType.INDIVIDUAL)
    .flatMap((group) =>
      (group.applicants ?? [])
        .filter((ba) => ba.applicantId === applicantId)
        .map((ba) => ({
          name: getBorrowerDisplayName(group, applicants),
          role: ba.role,
          roleLabel: BORROWER_ROLE_LABELS[ba.role] ?? ba.role,
        })),
    )
    .filter((membership) => membership.name);
};

export interface ApplicantEntitySectionMember {
  applicantId: string;
  role: BorrowerRoleEnum;
  /** Married/defacto partner's display name, when the applicant has one on this loan. */
  partnerName?: string;
  /** Role label, omitted when the role is a plain individual (no entity-specific role). */
  roleLabel?: string;
  /**
   * True on the one occurrence that renders the applicant's editable form and status. Later
   * occurrences (the applicant belongs to more than one entity) render a cross-reference only,
   * so per-applicant data (keyed by applicantId) is never bound to two forms at once.
   */
  isDetailAnchor: boolean;
  /** On a non-anchor occurrence, the entity whose section holds this applicant's form. */
  anchorEntityName?: string;
}

export interface ApplicantEntitySection {
  /** The company/trust entity heading. Absent for the trailing group of applicants in no entity. */
  entity?: { id: string; name: string };
  members: ApplicantEntitySectionMember[];
}

/**
 * Groups a set of applicants into entity-first sections for display: each company/trust
 * borrower group becomes a section titled with the entity name, listing the members that
 * belong to it; applicants in no company/trust fall into a trailing un-titled section.
 *
 * An applicant who belongs to more than one entity is listed under every entity they belong to,
 * with their role in each. Only the first occurrence is the detail anchor — it renders the
 * editable form and status; the rest are cross-references back to it, so per-applicant data
 * (keyed by applicantId) is never bound to two forms at once. Original applicant order is
 * preserved within the trailing un-grouped section.
 */
/**
 * Display names for entities whose own name fields are empty. An entity must never silently
 * vanish from the layout just because it has no name, so it falls back to its type.
 */
const ENTITY_FALLBACK_NAMES: Partial<Record<BorrowerTypeEnum, string>> = {
  [BorrowerType.COMPANY]: 'Company borrower',
  [BorrowerType.TRUST]: 'Trust borrower',
};

/**
 * Maps applicantId to their married/defacto partner's display name, collected across ALL
 * borrower groups including individual ones — an applicant's partner may be recorded on a
 * different entity's row (a director married to a trustee), or on a plain individual borrower.
 */
const collectPartnerNames = (borrowerGroups: BorrowerGroup[] | undefined): Map<string, string> => {
  const partnerNames = new Map<string, string>();

  for (const group of borrowerGroups ?? []) {
    for (const borrowerApplicant of group.applicants ?? []) {
      const partner = borrowerApplicant.marriedOrDefactoTo;
      if (!partner) continue;

      const name = partner.preferredName || [partner.firstName, partner.lastName].filter(Boolean).join(' ');
      if (name) partnerNames.set(borrowerApplicant.applicantId, name);
    }
  }

  return partnerNames;
};

export const groupApplicantsByEntity = (
  applicantIds: string[],
  borrowerGroups: BorrowerGroup[] | undefined,
  applicants: ApplicantEntry[],
): ApplicantEntitySection[] => {
  const anchoredTo = new Map<string, string>();
  const partnerNames = collectPartnerNames(borrowerGroups);
  const sections: ApplicantEntitySection[] = [];

  for (const group of borrowerGroups ?? []) {
    if (group.type === BorrowerType.INDIVIDUAL) continue;
    const name = getBorrowerDisplayName(group, applicants) || ENTITY_FALLBACK_NAMES[group.type] || 'Borrower';

    const members = (group.applicants ?? [])
      .filter((ba) => applicantIds.includes(ba.applicantId))
      .map((ba) => {
        const anchorEntityName = anchoredTo.get(ba.applicantId);
        const isDetailAnchor = anchorEntityName === undefined;
        if (isDetailAnchor) anchoredTo.set(ba.applicantId, name);
        return {
          applicantId: ba.applicantId,
          role: ba.role,
          roleLabel: ba.role === BorrowerRole.INDIVIDUAL ? undefined : BORROWER_ROLE_LABELS[ba.role] ?? ba.role,
          partnerName: partnerNames.get(ba.applicantId),
          isDetailAnchor,
          anchorEntityName,
        };
      });

    // An entity with no natural-person applicants at all (a company-trustee trust, say) is still
    // shown, with an empty member list, so it is never invisible. An entity whose applicants were
    // simply filtered out of this view is skipped instead.
    if (!members.length && (group.applicants ?? []).length) continue;

    sections.push({ entity: { id: group.id, name }, members });
  }

  const ungrouped = applicantIds
    .filter((id) => !anchoredTo.has(id))
    .map((applicantId) => ({
      applicantId,
      role: BorrowerRole.INDIVIDUAL,
      partnerName: partnerNames.get(applicantId),
      isDetailAnchor: true,
    }));
  if (ungrouped.length) sections.push({ members: ungrouped });

  return sections;
};

export const createFinancials = (): Financials => ({
  income: null,
  expenses: null,
  assets: [],
  liabilities: [],
});

export function createLoanApp(params?: {
  authorisedUser?: Record<string, string>;
  actionType?: ActionType;
  userType?: AppUser;
  brokerId?: string;
}): LoanApplication {
  const newApplicant = createApplicant(ApplicantType.PRIMARY);
  return {
    additionalFundsRequired: {} as AdditionalFundsRequiredDto,
    structuralVersionNumber: parseInt(import.meta.env.VITE_STRUCTURAL_VERSION_NUMBER || '0'),
    applicationId: 'application',
    actionType: params?.actionType ?? 'createNew',
    userType: params?.userType ?? null,
    incomingProperties: [],
    outgoingProperties: [],
    supportingProperties: [],
    applicants: [],
    borrowerGroups: [],
    propertiesOwnership: [],
    financials: { [newApplicant.id]: createFinancials() },
    entityAssets: {},
    entityIncome: {},
    endDebt: 0,
    netSurplus: 0,
    verificationCode: '',
    brokerId: params?.brokerId ?? '',
    summaryNotes: '',
    utmMetrics: {} as UtmMetricDto,
    allowNoContractOfSale: allowNoContractOfSale(),
  };
}

export const defaultAdditionalFundRequired = () => ({
  description: '',
  fundsRequiredDate: '',
  fundsRequiredAmount: 0,
  additionalFundsPurposeId: '',
});

export const defaultLoanDetails = () => ({
  savingsAmount: 0,
  giftedAmount: 0,
  reasonDescription: '',
  brokerFeePercentage: 0,
  borrowAmount: 0,
  loanPeriod: undefined,
  loanDate: '',
  calcLoanAmount: 0,
});

export const defaultPeriodicValue = (): PeriodicValue => ({
  amount: 0,
  frequency: 'month',
});

export const defaultAddress = (): Address => ({} as Address);

export const defaultIncomingProperty = (): IncomingProperty => ({
  id: '',
  settlementDate: '',
  propertyType: 'residential',
  address: defaultAddress(),
  propertyPurpose: 'owner_occupied',
  contractForSale: false,
  purchaseAmount: 0,
  depositAmount: 0,
  depositType: 'cash_payment',
  stampDutyStatus: 'already_paid',
  stampDuty: 0,
  securityHeldStatus: 'security_not_held',
  commercialType: '',
});

export const defaultOutgoingProperty = (): OutgoingProperty => ({
  id: '',
  propertyType: 'residential',
  address: defaultAddress(),
  propertyPurpose: 'owner_occupied',
  outstandingLoanAmount: 0,
  saleStatus: 'no',
  settlementDate: '',
  saleAmount: 0,
  depositAmount: 0,
  depositInTrust: false,
  securityHeldStatus: 'security_not_held',
  commercialType: '',
});

export const defaultSupportingProperty = (): SupportingProperty => ({
  id: '',
  propertyType: 'residential',
  address: defaultAddress(),
  propertyPurpose: 'owner_occupied',
  outstandingLoanAmount: 0,
  supportingPropertyValue: 0,
  securityHeldStatus: 'security_not_held',
});

export const defaultApplicant = (): Applicant => ({
  id: '',
  type: ApplicantType.PRIMARY,
  firstName: '',
  middleName: '',
  lastName: '',
  preferredName: '',
  gender: Gender.PREFER_NOT_TO_SAY,
  genderOther: '',
  birthdate: '',
  address: defaultAddress(),
  mobile: '',
  email: '',
  confirmEmail: '',
  numberOfDependants: 0,
  relationshipStatus: RelationshipStatus.SINGLE,
  relationshipToPrimary: undefined,
  relationshipOtherInfo: '',
});

export const defaultUnaccreditedBrokerProfile = (): UnaccreditedBrokerProfile => ({
  id: '',
  firstName: '',
  lastName: '',
  mobile: '',
  email: '',
  aggregator: undefined,
  state: undefined,
});

export const defaultIncome = (): ApplicantIncome => ({
  applicantId: '',
  employment: [],
  shortTermChange: false,
  shortTermEmploymentChange: '',
  centrelinkPayments: defaultPeriodicValue(),
  childSupportMaintenance: defaultPeriodicValue(),
  familyAllowance: defaultPeriodicValue(),
  familyTaxBenefit: defaultPeriodicValue(),
  governmentPension: defaultPeriodicValue(),
  dividendsInvestments: defaultPeriodicValue(),
  superannuation: defaultPeriodicValue(),
  other: defaultPeriodicValue(),
  expectIncomeChange: false,
  effectOnIncomeChange: '',
  incomeExpectation: {},
  employmentDetail: {},
});

export const defaultExpenses = (): ApplicantExpense => ({
  numberOfDependants: 0,
  groceries: defaultPeriodicValue(),
  clothing: defaultPeriodicValue(),
  education: defaultPeriodicValue(),
  insurancePremium: defaultPeriodicValue(),
  childcare: defaultPeriodicValue(),
  medical: defaultPeriodicValue(),
  recreation: defaultPeriodicValue(),
  communication: defaultPeriodicValue(),
  homePropertyCost: defaultPeriodicValue(),
  investmentPropertyCost: defaultPeriodicValue(),
  transport: defaultPeriodicValue(),
  other: defaultPeriodicValue(),
  predictedExpenseChange: defaultPeriodicValue(),
});

export const defaultPropertyAsset = (): PropertyAsset => ({
  id: '',
  type: AssetType.PROPERTY,
  address: defaultAddress(),
  propertyValue: 0,
  propertyPurpose: 'investment_property',
  rentalIncome: defaultPeriodicValue(),
  amountOwing: 0,
  assetNumber: 0,
});

export const defaultAssetAccount = (): AssetAccount => ({
  id: '',
  type: AssetType.SAVINGS,
  institution: '',
  value: 0,
  assetNumber: 0,
});

export const defaultApplicantIncome = (): ApplicantIncome => ({
  applicantId: '',
  employment: [],
  shortTermChange: false,
  shortTermEmploymentChange: '',
  centrelinkPayments: defaultPeriodicValue(),
  childSupportMaintenance: defaultPeriodicValue(),
  familyAllowance: defaultPeriodicValue(),
  familyTaxBenefit: defaultPeriodicValue(),
  governmentPension: defaultPeriodicValue(),
  dividendsInvestments: defaultPeriodicValue(),
  superannuation: defaultPeriodicValue(),
  other: defaultPeriodicValue(),
  expectIncomeChange: false,
  effectOnIncomeChange: '',
  incomeExpectation: {},
  employmentDetail: {},
});

/** Creates an empty RetirementDetails with sensible defaults. */
export const defaultRetirementDetails = (): RetirementDetails => ({
  plannedRetirementAge: undefined,
  incomeChangesBeforeRetirement: undefined,
  incomeChangesBeforeRetirementDescription: '',
  retirementIncomeImpact: '',
  expectedRetirementIncome: { frequency: 'month' },
  financialChangesDuringLoan: undefined,
  financialChangesDuringLoanDescription: '',
});

/**
 * Creates default company income object for corporate borrowers (Company/Trust with Company trustee)
 * Used to store net profit for the most recent financial year
 */
export const createCompanyIncome = (borrowerGroupId = ''): CompanyIncome => ({
  netProfit: { amount: 0, frequency: NET_PROFIT_DEFAULT_FREQUENCY },
  borrowerGroupId,
});

/**
 * Company/Trust net profit keyed by borrower group. Drafts saved before `entityIncome` kept it on the group's
 * first applicant's financials, so that is read as a fallback.
 */
export const getEntityIncome = (data: Pick<LoanApplication, 'financials' | 'entityIncome'>): EntityIncome => ({
  ...Object.fromEntries(
    Object.values(data.financials ?? {}).flatMap((financials) => {
      const companyIncome = financials?.companyIncome;
      return companyIncome?.borrowerGroupId ? [[companyIncome.borrowerGroupId, companyIncome]] : [];
    }),
  ),
  ...data.entityIncome,
});

/** The net profit of the given borrower groups only, so a removed company or trust cannot contribute stale income. */
export const getActiveEntityIncome = (
  data: Pick<LoanApplication, 'financials' | 'entityIncome'>,
  borrowerGroups: BorrowerGroup[],
): EntityIncome => {
  const groupIds = new Set(borrowerGroups.map((group) => group.id));
  return Object.fromEntries(Object.entries(getEntityIncome(data)).filter(([groupId]) => groupIds.has(groupId)));
};

/** Financials without the per-applicant net profit older drafts kept, once it has moved to `entityIncome`. */
export const withoutLegacyCompanyIncome = (financials: ApplicantFinancials): ApplicantFinancials =>
  mapValues(financials, (entry) => omit(entry, 'companyIncome')) as ApplicantFinancials;
